import { Controller } from "@hotwired/stimulus"

/**
 * Auto-save controller with visual status indicator
 * Handles saving on tab switch, conflict resolution, and user feedback
 * NOTE: Does NOT auto-save on field changes - only saves when switching tabs
 */
export default class extends Controller {
  static targets = ["status", "statusIcon", "statusText", "form", "overlay"]
  static values = {
    url: String,
    interval: { type: Number, default: 2000 }, // Debounce delay in ms
    saving: { type: Boolean, default: false },
    lockVersion: Number
  }

  // Icon classes for different states
  static icons = {
    pending: 'fa fa-clock-o',
    saving: 'fa fa-spinner fa-spin',
    success: 'fa fa-check-circle',
    error: 'fa fa-exclamation-circle'
  }

  connect() {
    // Create debounced save function
    this.debouncedSave = this.debounce(this.performSave.bind(this), this.intervalValue)

    // Track last save time
    this.lastSaveTime = null

    // Set up tab change listener to save before switching tabs
    // This is the ONLY trigger for autosave - no field change listeners
    this.setupTabChangeListener()
  }

  disconnect() {
    // Clear any pending saves
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
      this.saveTimeout = null
    }

    // Clear debounced function
    if (this.debouncedSave) {
      this.debouncedSave.cancel && this.debouncedSave.cancel()
    }

    // Clean up tab change listener
    this.teardownTabChangeListener()
  }

  /**
   * Trigger auto-save (debounced)
   * Called on form change events
   */
  save() {
    if (this.savingValue) {
      // Mark that another save was requested
      this.saveAgainAfterCurrent = true
      return
    }

    this.showStatus('Waiting to save...', 'pending')
    this.debouncedSave()
  }

  /**
   * Force immediate save (bypass debounce)
   * @returns {Promise} Save promise
   */
  async saveNow() {
    // Cancel any pending debounced save
    if (this.debouncedSave && this.debouncedSave.cancel) {
      this.debouncedSave.cancel()
    }

    return this.performSave()
  }

  /**
   * Perform the actual save operation
   * @returns {Promise} Save promise
   */
  async performSave() {
    if (this.savingValue) {
      return Promise.resolve()
    }

    if (!this.hasFormTarget) {
      return Promise.reject(new Error('No form target'))
    }

    this.savingValue = true
    this.showStatus('Saving...', 'saving')

    try {
      // Temporarily disable fields marked as autosave-ignore so they're excluded from FormData
      const ignoredFields = this.formTarget.querySelectorAll('[data-autosave-ignore="true"]')
      const originalIgnoredStates = new Map()

      ignoredFields.forEach(field => {
        originalIgnoredStates.set(field, field.disabled)
        field.disabled = true
      })

      const formData = new FormData(this.formTarget)

      // Re-enable ignored fields immediately after FormData creation
      originalIgnoredStates.forEach((wasDisabled, field) => {
        field.disabled = wasDisabled
      })

      // Now disable form during save to prevent conflicts
      this.disableForm()

      // Add lock_version for optimistic locking
      if (this.hasLockVersionValue) {
        formData.set('report[lock_version]', this.lockVersionValue)
      }

      const response = await fetch(this.urlValue, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': this.csrfToken,
          'Accept': 'application/json'
        },
        body: formData
      })

      // Check content type before parsing
      const contentType = response.headers.get('Content-Type')
      let data

      if (contentType && contentType.includes('application/json')) {
        data = await response.json()
      } else {
        // Server returned HTML (likely an error page)
        const htmlText = await response.text()

        // Try to extract error message from HTML
        const errorMatch = htmlText.match(/<h1[^>]*>(.*?)<\/h1>/i)
        const errorTitle = errorMatch ? errorMatch[1] : 'Unknown error'

        throw new Error(`Server error (${response.status}): ${errorTitle}`)
      }

      if (response.ok && data.status === 'success') {
        this.handleSaveSuccess(data)
      } else if (data.status === 'conflict') {
        this.handleSaveConflict(data)
      } else {
        this.handleSaveError(data)
      }
    } catch (error) {
      this.handleNetworkError(error)
    } finally {
      this.savingValue = false
      // Re-enable form after save completes
      this.enableForm()

      // Check if another save was requested during this save
      if (this.saveAgainAfterCurrent) {
        this.saveAgainAfterCurrent = false
        // Trigger debounced save to avoid rapid-fire saves
        this.debouncedSave()
      }
    }
  }

  /**
   * Handle successful save
   * @param {Object} data - Response data
   */
  handleSaveSuccess(data) {
    // Update indication IDs in the form (CRITICAL for nested attributes)
    if (data.indication_ids) {
      this.updateIndicationIds(data.indication_ids)
    }

    // Update ET probe configuration IDs in the form (CRITICAL for nested attributes - prevents duplicates)
    if (data.et_probe_configuration_ids) {
      this.updateProbeConfigurationIds(data.et_probe_configuration_ids)
    }

    // Update lock version in both Stimulus value AND hidden field
    if (data.lock_version) {
      this.lockVersionValue = data.lock_version

      // CRITICAL: Also update the hidden field in the form
      // Otherwise FormData will read the stale value on next save
      const lockVersionField = this.formTarget.querySelector('input[name="report[lock_version]"]')
      if (lockVersionField) {
        lockVersionField.value = data.lock_version
      }
    }

    // Update last save time
    this.lastSaveTime = new Date(data.saved_at || Date.now())

    // Show success message with time
    const timeAgo = data.saved_at_human || 'just now'
    this.showStatus(`Saved ${timeAgo}`, 'success')

    // Update progress if provided
    if (data.progress_percentage !== undefined) {
      this.dispatch('progress-updated', {
        detail: { percentage: data.progress_percentage }
      })
    }

    // Auto-hide success message after 3 seconds
    setTimeout(() => {
      if (this.currentStatusMessage === `Saved ${timeAgo}`) {
        this.hideStatus()
      }
    }, 3000)
  }

  /**
   * Handle save conflict (optimistic locking)
   * @param {Object} data - Response data
   */
  handleSaveConflict(data) {
    // Show conflict message
    this.showStatus(
      'Conflict: Another user modified this report. Please refresh the page.',
      'error'
    )

    // Offer to reload
    const shouldReload = confirm(
      data.message || 'The report was modified by another user. Reload to see the latest version?'
    )

    if (shouldReload) {
      window.location.reload()
    }
  }

  /**
   * Handle save error
   * @param {Object} data - Response data
   */
  handleSaveError(data) {
    // Check if this is a transient RecordNotFound error (deleted/stale indication IDs)
    const errorString = JSON.stringify(data).toLowerCase()
    const isIndicationNotFound = errorString.includes('couldn\'t find') &&
                                 errorString.includes('indication')

    if (isIndicationNotFound) {
      // Don't show scary error to user
      this.showStatus('Syncing...', 'pending')

      // Get current tab index and reload with hash to preserve it
      const tabsContainer = document.querySelector('#wizard-tabs-container')
      let tabIndex = 0

      if (tabsContainer) {
        const activeTab = tabsContainer.querySelector('.nav-link.active')
        if (activeTab) {
          const allTabs = tabsContainer.querySelectorAll('.nav-link')
          tabIndex = Array.from(allTabs).indexOf(activeTab)
        }
      }

      // Set a flag that we're doing a sync reload
      sessionStorage.setItem('autosave_sync_reload_tab', tabIndex.toString())

      setTimeout(() => {
        window.location.reload()
      }, 500)
      return
    }

    // Show actual errors to the user
    const errorMessage = data.errors && data.errors.length > 0
      ? data.errors[0]
      : 'Failed to save changes'

    this.showStatus(`Error: ${errorMessage}`, 'error')
  }

  /**
   * Handle network error
   * @param {Error} error - Network error
   */
  handleNetworkError(error) {
    this.showStatus(
      'Network error. Changes not saved. Will retry on next change.',
      'error'
    )

    // Auto-retry after 5 seconds if still online
    setTimeout(() => {
      if (navigator.onLine) {
        this.save()
      }
    }, 5000)
  }

  /**
   * Show status message
   * @param {string} message - Status message
   * @param {string} type - Status type (pending, saving, success, error)
   */
  showStatus(message, type = 'info') {
    if (!this.hasStatusTarget) return

    // Update icon
    if (this.hasStatusIconTarget) {
      this.statusIconTarget.className = this.constructor.icons[type] || 'fa fa-info-circle'
    }

    // Update text
    if (this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = message
    }

    // Update container classes
    this.statusTarget.className = `autosave-status autosave-status--${type}`
    this.statusTarget.classList.remove('hidden')

    this.currentStatusMessage = message
  }

  /**
   * Hide status message
   */
  hideStatus() {
    if (!this.hasStatusTarget) return

    this.statusTarget.classList.add('hidden')
    this.currentStatusMessage = ''
  }

  /**
   * Disable form inputs during save
   */
  disableForm() {
    if (!this.hasFormTarget) return

    // Show blocking overlay
    this.showOverlay()

    // Add saving class for visual feedback
    this.formTarget.classList.add('form-saving')

    // Store original disabled states
    this.originalDisabledStates = new Map()

    // Disable all form inputs, selects, and textareas
    const inputs = this.formTarget.querySelectorAll('input:not([type="hidden"]), select, textarea, button[type="submit"]')
    inputs.forEach(input => {
      this.originalDisabledStates.set(input, input.disabled)
      input.disabled = true
    })
  }

  /**
   * Re-enable form inputs after save
   */
  enableForm() {
    if (!this.hasFormTarget) return

    // Hide blocking overlay
    this.hideOverlay()

    // Remove saving class
    this.formTarget.classList.remove('form-saving')

    // Restore original disabled states
    if (this.originalDisabledStates) {
      this.originalDisabledStates.forEach((wasDisabled, input) => {
        input.disabled = wasDisabled
      })
      this.originalDisabledStates.clear()
    }
  }

  /**
   * Show blocking overlay during save
   */
  showOverlay() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.style.display = 'flex'
    }
  }

  /**
   * Hide blocking overlay after save
   */
  hideOverlay() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.style.display = 'none'
    }
  }

  /**
   * Debounce function
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in ms
   * @returns {Function} Debounced function
   */
  debounce(func, wait) {
    let timeout

    const debounced = (...args) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => func.apply(this, args), wait)
    }

    debounced.cancel = () => {
      clearTimeout(timeout)
    }

    return debounced
  }

  /**
   * Get CSRF token
   * @returns {string} CSRF token
   */
  get csrfToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.content : ''
  }

  /**
   * Set up tab change listener
   * Saves form data before switching tabs to ensure no data is lost
   * Listens for tabs:before-change event dispatched by the tabs controller
   */
  setupTabChangeListener() {
    // Create bound handler for cleanup
    this.boundTabChangeHandler = async (e) => {
      // Perform immediate save (bypass debounce) to ensure data is persisted before tab switch
      await this.saveNow()
    }

    // Listen on the element (which contains the tabs container)
    this.element.addEventListener('tabs:before-change', this.boundTabChangeHandler)
  }

  /**
   * Clean up tab change listener
   */
  teardownTabChangeListener() {
    if (!this.boundTabChangeHandler) {
      return
    }

    this.element.removeEventListener('tabs:before-change', this.boundTabChangeHandler)
    this.boundTabChangeHandler = null
  }

  /**
   * Update indication ID fields in the form after save
   * This ensures that newly created indications have their IDs updated in the form
   * @param {Object} indicationIds - Object with arrays of IDs for each indication type
   */
  updateIndicationIds(indicationIds) {
    const types = ['et', 'mt', 'pt', 'rt', 'ut', 'vt', 'pmi']

    // Map type to the nested attributes field name pattern
    // PMI uses pmi_measurements_attributes instead of pmi_indications_attributes
    const attributeNameMap = {
      pmi: 'pmi_measurements_attributes'
    }

    types.forEach(type => {
      if (!indicationIds[type] || indicationIds[type].length === 0) {
        return
      }

      const ids = indicationIds[type]
      const containerSelector = `#${type}_indications`
      const container = this.formTarget.querySelector(containerSelector)
      const attrName = attributeNameMap[type] || `${type}_indications_attributes`

      if (!container) {
        return
      }

      // Find all indication fieldsets in this container (not marked for deletion)
      const fieldsets = Array.from(container.querySelectorAll('.nested-fields'))
        .filter(fs => {
          // Check if marked for deletion
          const destroyField = fs.querySelector('input[name*="_destroy"]')
          if (destroyField) {
            const isMarkedForDestroy = destroyField.value === '1' ||
                                       destroyField.value === 'true' ||
                                       destroyField.checked === true
            if (isMarkedForDestroy) {
              return false
            }
          }

          // Check if hidden by Cocoon (display:none or hidden class)
          const isHidden = fs.style.display === 'none' ||
                          fs.classList.contains('hidden') ||
                          fs.classList.contains('removed-indication')
          if (isHidden) {
            return false
          }

          return true
        })

      fieldsets.forEach((fieldset, index) => {
        if (index >= ids.length) {
          return
        }

        // Find the hidden ID field
        let idField = fieldset.querySelector(`input[name*="${attrName}"][name*="[id]"]`)
        const newId = ids[index]

        if (!idField) {
          // Create the hidden ID field (Cocoon doesn't create it for new records)
          const anyInput = fieldset.querySelector(`input[name*="${attrName}"]`)
          if (!anyInput) {
            return
          }

          // Extract the base name pattern
          const nameMatch = anyInput.name.match(new RegExp(`(.*\\[${attrName}\\]\\[\\d+\\])`))
          if (!nameMatch) {
            return
          }

          const baseName = nameMatch[1]

          // Create and insert the hidden ID field
          idField = document.createElement('input')
          idField.type = 'hidden'
          idField.name = `${baseName}[id]`
          idField.value = newId
          fieldset.insertBefore(idField, fieldset.firstChild)
          return
        }

        const oldValue = idField.value

        // Update the ID if different
        if (oldValue !== String(newId)) {
          idField.value = newId
        }
      })
    })
  }

  /**
   * Update ET probe configuration ID fields in the form after save
   * This ensures that newly created probe configurations have their IDs updated in the form,
   * preventing duplicate creation on subsequent saves.
   * @param {Array} probeConfigIds - Array of probe configuration IDs
   */
  updateProbeConfigurationIds(probeConfigIds) {
    if (!probeConfigIds || probeConfigIds.length === 0) {
      return
    }

    const container = this.formTarget.querySelector('#et_probe_configurations')
    if (!container) {
      return
    }

    // Find all probe configuration fieldsets (not marked for deletion)
    const fieldsets = Array.from(container.querySelectorAll('.nested-fields'))
      .filter(fs => {
        // Check if marked for deletion
        const destroyField = fs.querySelector('input[name*="_destroy"]')
        if (destroyField) {
          const isMarkedForDestroy = destroyField.value === '1' ||
                                     destroyField.value === 'true' ||
                                     destroyField.checked === true
          if (isMarkedForDestroy) {
            return false
          }
        }

        // Check if hidden by Cocoon (display:none or hidden class)
        const isHidden = fs.style.display === 'none' ||
                        fs.classList.contains('hidden') ||
                        fs.classList.contains('removed')
        if (isHidden) {
          return false
        }

        return true
      })

    fieldsets.forEach((fieldset, index) => {
      if (index >= probeConfigIds.length) {
        return
      }

      // Find the hidden ID field
      let idField = fieldset.querySelector('input[name*="et_probe_configurations_attributes"][name*="[id]"]')
      const newId = probeConfigIds[index]

      if (!idField) {
        // Create the hidden ID field (Cocoon doesn't create it for new records)
        const anyInput = fieldset.querySelector('input[name*="et_probe_configurations_attributes"]')
        if (!anyInput) {
          return
        }

        // Extract the base name pattern
        const nameMatch = anyInput.name.match(/(.*\[et_probe_configurations_attributes\]\[\d+\])/)
        if (!nameMatch) {
          return
        }

        const baseName = nameMatch[1]

        // Create and insert the hidden ID field
        idField = document.createElement('input')
        idField.type = 'hidden'
        idField.name = `${baseName}[id]`
        idField.value = newId
        fieldset.insertBefore(idField, fieldset.firstChild)
        return
      }

      const oldValue = idField.value

      // Update the ID if different
      if (oldValue !== String(newId)) {
        idField.value = newId
      }
    })
  }

  /**
   * Get last save time as human-readable string
   * @returns {string} Time ago string
   */
  get lastSaveTimeAgo() {
    if (!this.lastSaveTime) return 'never'

    const seconds = Math.floor((Date.now() - this.lastSaveTime) / 1000)

    if (seconds < 10) return 'just now'
    if (seconds < 60) return `${seconds} seconds ago`
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`
    return `${Math.floor(seconds / 3600)} hours ago`
  }
}
